import { useCallback, useReducer } from "react";
import produce from "immer";
import _ from "lodash";

// TODO: move
type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type State = {
	hasMore: boolean;
	limit: number;
	offset: number;
	search?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	results: any[];
	loading: boolean;
	searching: boolean;
	error: null | string;
	totalCount: number;
};

type ResetState = Partial<State>;

export type PageData = PartialBy<
	Omit<State, "loading" | "searching" | "error">,
	"offset" | "limit" | "search" | "totalCount"
>;

// Actions
const SET_DATA = "SET_DATA";
const APPEND_DATA = "APPEND_DATA";
const PATCH_STATE = "PATCH_STATE";
const ERROR = "ERROR";
const CLEAR_ERROR = "CLEAR_ERROR";
const STATUS_LOADING = "STATUS_LOADING";
const STATUS_SEARCHING = "STATUS_SEARCHING";
const STATUS_READY = "STATUS_READY";
const RESET = "RESET";

interface SetDataAction {
	type: typeof SET_DATA;
	data: PageData;
}
interface AppendDataAction {
	type: typeof APPEND_DATA;
	data: PageData;
}
interface PatchStateAction {
	type: typeof PATCH_STATE;
	state: State;
}
interface ErrorAction {
	type: typeof ERROR;
	message: string;
}
interface SetLoadingAction {
	type: typeof STATUS_LOADING;
}

interface SetSearchingAction {
	type: typeof STATUS_SEARCHING;
}
interface SetReadyAction {
	type: typeof STATUS_READY;
}
interface ClearErrorAction {
	type: typeof CLEAR_ERROR;
}
interface ResetAction {
	type: typeof RESET;
	data: ResetState;
}

type Actions =
	| SetDataAction
	| AppendDataAction
	| PatchStateAction
	| ErrorAction
	| SetLoadingAction
	| SetSearchingAction
	| SetReadyAction
	| ClearErrorAction
	| ResetAction;

const initialState: State = {
	hasMore: false,
	limit: 10,
	offset: 0,
	results: [],
	loading: true,
	searching: false,
	error: null,
	totalCount: 0,
};

export const reducer = (state: State, action: Actions): State => {
	// If you're patching the state it's your responsibility to
	// ensure immutability
	if (action.type === PATCH_STATE) return action.state;

	return produce(state, draft => {
		switch (action.type) {
			case SET_DATA: {
				const { results, ...rest } = action.data;
				_.merge(draft, rest);
				draft.results = results;
				break;
			}

			case APPEND_DATA:
				const { results, ...rest } = action.data;
				// draft.results = [...draft.results, ...results];
				for (const item of results) {
					draft.results.push(item);
				}
				Object.assign(draft, rest);
				// _.assignWith(draft, rest);
				break;

			case STATUS_LOADING:
				draft.loading = true;
				break;

			case STATUS_SEARCHING:
				draft.searching = true;
				break;

			case STATUS_READY:
				draft.loading = false;
				draft.searching = false;
				break;

			case ERROR:
				draft.error = action.message;
				break;

			case CLEAR_ERROR:
				draft.error = null;
				break;
			case RESET:
				const init = _.merge(initialState, action.data);
				_.merge(draft, init);
				draft.results = init.results;
				break;
			default:
				return draft;
		}
	});
};

export default (init: State = initialState) => {
	const [state, dispatch] = useReducer(reducer, init);

	const setData = useCallback(async (data: PageData) => {
		dispatch({ type: SET_DATA, data });
	}, []);

	const addPage = useCallback(async (data: PageData) => {
		dispatch({ type: APPEND_DATA, data });
	}, []);

	const loading = useCallback(() => {
		dispatch({ type: STATUS_LOADING });
	}, []);

	const searching = useCallback(() => {
		dispatch({ type: STATUS_SEARCHING });
	}, []);

	const ready = useCallback(() => {
		dispatch({ type: STATUS_READY });
	}, []);

	const setError = useCallback((message: string) => {
		dispatch({ type: ERROR, message });
	}, []);

	const clearError = useCallback(() => {
		dispatch({ type: CLEAR_ERROR });
	}, []);

	const patchState = useCallback((update: State) => {
		dispatch({ type: PATCH_STATE, state: update });
	}, []);

	const reset = useCallback(async (data: ResetState = {}) => {
		dispatch({ type: RESET, data });
	}, []);

	return {
		state,
		setData,
		addPage,
		patchState,
		loading,
		searching,
		ready,
		setError,
		clearError,
		reset,
	};
};
